// =============================================================================
// HOME — Die eigentliche Startseite
// -----------------------------------------------------------------------------
// Aufbau (Reihenfolge bestimmt die Anzeige):
//   1. Logo mit Glitch-Effekt (3 überlagerte Bilder: weiß / cyan / rot)
//   2. Tagline (Genres)
//   3. Booking-Karte (E-Mail-Anfrage)
//   4. Upcoming Events (lädt aus Google Sheet)
//   5. Radio-Shows (TechnoBase, HouseTime)
//   6. Socials (Instagram, Facebook, Mixcloud, ...)
//   7. Discord-Button
//   8. Ko-fi-Button
//   9. Footer (Impressum, Datenschutz, Copyright)
// =============================================================================

import { Link } from "react-router-dom";
import { DiscordIcon, KofiIcon, ClockIcon, MailIcon } from "@/icons";
import {
  LOGO_PATH,
  TAGLINE,
  RADIO_SHOWS,
  SOCIALS,
  BOOKING,
  DISCORD_URL,
  KOFI_URL,
} from "@/config";
import Background from "@/components/Background";
import UpcomingEvents from "@/components/UpcomingEvents";

/** Erzeugt den vollständigen mailto-Link mit Betreff und Body */
const bookingMailto =
  `mailto:${BOOKING.email}` +
  `?subject=${encodeURIComponent(BOOKING.subject)}` +
  `&body=${encodeURIComponent(BOOKING.body)}`;

export default function Home() {
  return (
    <div data-testid="petsch-landing">
      <Background />

      <main className="page">

        {/* ============================================================ */}
        {/* 1. LOGO MIT GLITCH-EFFEKT (3 überlagerte Bilder)                */}
        {/* ============================================================ */}
        <div className="logo-wrap reveal d1" data-testid="logo">
          <img src={LOGO_PATH} alt="Petsch" className="logo-img-clone red" />
          <img src={LOGO_PATH} alt="" className="logo-img-clone cyan" aria-hidden="true" />
          <img src={LOGO_PATH} alt="" className="logo-img" aria-hidden="true" />
        </div>

        {/* ============================================================ */}
        {/* 2. TAGLINE                                                      */}
        {/* ============================================================ */}
        <div className="tagline reveal d2" data-testid="tagline">
          <span className="dot" />
          {TAGLINE.map((word, idx) => (
            <span key={word} style={{ display: "contents" }}>
              <span>{word}</span>
              {idx < TAGLINE.length - 1 && <span className="accent">·</span>}
            </span>
          ))}
          <span className="dot" />
        </div>

        {/* ============================================================ */}
        {/* 3. BOOKING — E-Mail-Anfrage mit vorausgefülltem Template       */}
        {/* ============================================================ */}
        <section className="section-block reveal d3" data-testid="section-booking">
          <h2 className="section-title">Booking</h2>
          <div className="booking-card">
            <span className="booking-label">
              <MailIcon size={14} aria-hidden="true" />
              Booking Request:
            </span>
            <a
              href={bookingMailto}
              className="booking-email"
              data-testid="booking-email-link"
            >
              {BOOKING.email}
            </a>
            <a
              href={bookingMailto}
              className="booking-btn"
              data-testid="booking-button"
            >
              <MailIcon size={14} aria-hidden="true" />
              <span>Send E-Mail</span>
            </a>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 4. UPCOMING EVENTS (lädt aus Google Sheet)                      */}
        {/* ============================================================ */}
        <UpcomingEvents />

        {/* ============================================================ */}
        {/* 5. RADIO SHOWS                                                  */}
        {/* ============================================================ */}
        <section className="section-block reveal d3" data-testid="section-radio">
          <h2 className="section-title">Radio Shows</h2>
          <div className="radio-grid">
            {RADIO_SHOWS.map((r) => (
              <a
                key={r.name}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="radio-card"
                data-testid={r.testId}
              >
                <div className="radio-meta">
                  <span className="label">{r.label}</span>
                  <img
                    src={r.img}
                    alt={`${r.name} logo`}
                    className={`radio-logo ${r.testId === "radio-technobase" ? "tb" : ""}`}
                  />
                  <span className="schedule">
                    <ClockIcon size={12} aria-hidden="true" />
                    {r.schedule}
                  </span>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* ============================================================ */}
        {/* 6. SOCIALS                                                      */}
        {/* ============================================================ */}
        <section className="section-block reveal d4" data-testid="section-socials">
          <h2 className="section-title">Follow & Listen</h2>
          <div className="social-grid">
            {SOCIALS.map((s) => (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="social-tile"
                data-testid={s.testId}
                aria-label={s.name}
              >
                <span className="social-icon" aria-hidden="true">
                  <s.Icon />
                </span>
                <span className="handle">{s.name}</span>
              </a>
            ))}
          </div>
        </section>

        {/* ============================================================ */}
        {/* 7. DISCORD-BUTTON (outlined-Variante)                           */}
        {/* ============================================================ */}
        <section className="kofi-wrap reveal d5" data-testid="section-discord">
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="kofi-btn kofi-btn-outline"
            data-testid="discord-button"
          >
            <DiscordIcon aria-hidden="true" />
            <span>Join my Discord</span>
          </a>
        </section>

        {/* ============================================================ */}
        {/* 8. KO-FI BUTTON                                                 */}
        {/* ============================================================ */}
        <section className="kofi-wrap reveal d5" data-testid="section-kofi">
          <a
            href={KOFI_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="kofi-btn"
            data-testid="kofi-button"
          >
            <KofiIcon aria-hidden="true" />
            <span>Support me on Ko-fi</span>
          </a>
        </section>

        {/* ============================================================ */}
        {/* 9. FOOTER                                                       */}
        {/* ============================================================ */}
        <footer className="foot reveal d6" data-testid="footer">
          <div className="row">
            <Link to="/impressum" data-testid="footer-impressum-link">
              Impressum
            </Link>
            <span>·</span>
            <Link to="/datenschutz" data-testid="footer-datenschutz-link">
              Datenschutz
            </Link>
            <span>·</span>
            <span>© {new Date().getFullYear()} Petsch</span>
          </div>
        </footer>
      </main>
    </div>
  );
}
